import * as React from 'react';
import { useNavigate } from 'react-router';
import { Ic } from './Icons';
import useNotifications from '../../hooks/useNotifications';
import NotificationRow from '../notifications/NotificationRow';
import { badgeModel } from '../notifications/notifUtils';
import { navClickHandler } from '../../utils/navClick';

// Scoped responsive swap, mirroring OrgSwitcher: BOTH bells live in the DOM and
// CSS decides which is visible per viewport.
//   desktop → the bell is ALWAYS shown (permanent affordance, badge only when unseen)
//   mobile  → also ALWAYS shown (#308: it used to hide once there was nothing
//             unseen, which meant a mobile user who'd already seen their
//             notifications had no way at all to reopen the panel from the top
//             bar — the badge, not the bell itself, is what should reflect
//             unseen state).
const RESPONSIVE_STYLE = `
  .notif-bell-desktop { display: inline-grid; }
  .notif-bell-mobile { display: none; }
  @media (max-width: 640px) {
    .notif-bell-desktop { display: none; }
    .notif-bell-mobile { display: inline-grid; }
  }
`;

function Badge({ unseen }) {
  const model = badgeModel(unseen);
  if (model.kind === 'none') return null;
  if (model.kind === 'dot') {
    return <span className="notif-badge dot" data-testid="notif-badge-dot" aria-hidden="true" />;
  }
  return <span className="notif-badge" data-testid="notif-badge-count">{model.text}</span>;
}

/**
 * The top-bar notification bell + popover (#259).
 *
 * Polls the cheap unseen-count for the badge; loads the (active) list when the
 * popover opens. Each row arms a per-row IntersectionObserver so it flips to
 * `seen` only once it genuinely scrolls into view (`queueSeen`). "View all"
 * routes to the Settings → Notifications page.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const { unseenCount, items, loading, load, queueSeen } = useNotifications({ poll: true });
  const [open, setOpen] = React.useState(false);

  const openPopover = () => {
    setOpen(true);
    load({ state: 'active', limit: 12 }).catch(() => { /* surfaced as empty */ });
  };

  const close = () => setOpen(false);

  // #793 H / #149: Escape closes the popover. It was previously dismissible only by
  // POINTER (clicking the scrim behind it, or the bell again) — which is exactly why
  // that scrim was a `<div onClick>` accessibility error. The scrim can only honestly
  // be marked decorative (`aria-hidden`, below) once the keyboard has its own exit.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // A notification may carry a deep link in `detail.links` (e.g. a new-comment
  // alert hard-links to `/item/<id>#comment-<id>`, #360). `detail` can arrive as
  // an object or a JSON string; tolerate both and only follow INTERNAL (SPA)
  // hrefs — anything else falls back to the Settings focus view.
  const internalLinkHref = (n) => {
    let d = n && n.detail;
    if (typeof d === 'string') {
      try { d = JSON.parse(d); } catch { return null; }
    }
    const links = d && Array.isArray(d.links) ? d.links : [];
    for (const l of links) {
      const href = typeof l === 'string' ? l : (l && (l.href || l.url));
      // #403: only real SPA routes count. Server/system notifications (e.g. the
      // embedding monitor) carry a BACKEND API url like `/api/iam/admin/embedding`
      // in `detail.links`; navigating there has no matching route, so the row
      // "did nothing". Exclude `/api/` so those fall through to the Settings focus
      // view (which resolves the notification by id) instead.
      if (typeof href === 'string' && href.startsWith('/') && !href.startsWith('/api/')) return href;
    }
    return null;
  };

  const goDetail = (n) => {
    close();
    const href = internalLinkHref(n);
    if (href) {
      navigate(href);
      return;
    }
    navigate(`/account/notifications?focus=${encodeURIComponent(n.id)}`);
  };

  const viewAll = () => {
    close();
    navigate('/account/notifications');
  };

  const bellBtn = (className, testid) => (
    <button
      type="button"
      className={`icon-btn notif-anchor ${className}`}
      data-testid={testid}
      title="Notifications"
      aria-label="Notifications"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => (open ? close() : openPopover())}
      style={{ borderColor: 'transparent', background: 'transparent' }}
    >
      <Ic name="bell" />
      <Badge unseen={unseenCount} />
    </button>
  );

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <style>{RESPONSIVE_STYLE}</style>
      {bellBtn('notif-bell-desktop', 'notif-bell-desktop')}
      {bellBtn('notif-bell-mobile', 'notif-bell-mobile')}

      {open && (
        <>
          <div className="notif-scrim" onClick={close} aria-hidden="true" />
          <div role="menu" className="cardp notif-pop" data-testid="notif-popover">
            <div className="notif-pop-head">
              <Ic name="bell" sm />
              <b>Notifications</b>
              <span className="grow" />
              {unseenCount > 0 && (
                <span className="badge accent">{unseenCount > 9 ? '9+' : unseenCount} new</span>
              )}
            </div>
            <div className="notif-pop-list">
              {loading && items.length === 0 ? (
                <div className="notif-empty">Loading…</div>
              ) : items.length === 0 ? (
                <div className="notif-empty">You&apos;re all caught up.</div>
              ) : (
                items.map((n) => (
                  <NotificationRow key={n.id} n={n} onVisible={queueSeen} onClick={goDetail} />
                ))
              )}
            </div>
            <div className="notif-pop-foot">
              <a
                className="btn ghost"
                href="/account/notifications"
                data-testid="notif-view-all"
                onClick={navClickHandler(viewAll)}
              >
                View all
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
